import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useWeb3 } from '../contexts/Web3Context';
import { sendContractTransaction, approveToken, formatTransactionError } from '../utils/transactionHelper';
import './BuyOrder.css';

const PAGE_SIZE = 30;

const BuyOrder = () => {
  const web3Context = useWeb3();
  const { account, contract, web3, usdtContract, CONTRACT_ADDRESS, USDT_ADDRESS } = web3Context;

  // Pagination & Order State
  const [allCandidateIds, setAllCandidateIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [displayedOrders, setDisplayedOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [totalOrderCount, setTotalOrderCount] = useState(0);

  // Selected Order & Execution State
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [buyAmount, setBuyAmount] = useState('');
  const [loadingBuy, setLoadingBuy] = useState(false);

  // Balances & Registration State
  const [usdtBalance, setUsdtBalance] = useState('0');
  const [tokenBalance, setTokenBalance] = useState('0');
  const [bnbBalance, setBnbBalance] = useState('0');
  const [isRegistered, setIsRegistered] = useState(true);
  const [registering, setRegistering] = useState(false);

  // Dynamic Offer State
  const [offerBonus, setOfferBonus] = useState(null);
  const [offerFee, setOfferFee] = useState(null);

  // Quick Search & Copy State
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedAddress, setCopiedAddress] = useState(null);

  // Load user wallet balances
  const loadBalances = useCallback(async () => {
    try {
      if (account && web3) {
        const bnbWei = await web3.eth.getBalance(account);
        setBnbBalance(web3.utils.fromWei(bnbWei.toString(), 'ether'));
      }
      if (usdtContract && account) {
        const bal = await usdtContract.methods.balanceOf(account).call();
        setUsdtBalance(web3.utils.fromWei(bal.toString(), 'ether'));
      }
      if (web3Context.tokenContract && account) {
        const tBal = await web3Context.tokenContract.methods.balanceOf(account).call();
        setTokenBalance(web3.utils.fromWei(tBal.toString(), 'ether'));
      }
    } catch (error) {
      console.error('Error loading balances:', error);
    }
  }, [usdtContract, web3Context.tokenContract, account, web3]);

  // Check user registration status on contract
  const checkUserRegistration = useCallback(async () => {
    if (!contract || !account) return;
    try {
      const u = await contract.methods.users(account).call();
      const reg = u.registered !== undefined ? u.registered : u[0];
      setIsRegistered(Boolean(reg));
    } catch (e) {
      console.warn('Registration status check error:', e);
    }
  }, [contract, account]);

  // 1. Initial Discovery of Order IDs (Reverse order: Newest first)
  const discoverAllOrderIds = useCallback(async () => {
    if (!contract || !web3) return;
    setLoadingOrders(true);
    try {
      let idsList = [];

      // Strategy 1: openOrders() array from contract
      try {
        const activeIds = await contract.methods.openOrders().call();
        if (activeIds && activeIds.length > 0) {
          idsList = Array.from(activeIds).map(id => id.toString());
        }
      } catch (err) {
        console.warn('openOrders call warning:', err);
      }

      // Strategy 2: Fallback query via orderIdCounter if openOrders is empty or partial
      try {
        const counter = await contract.methods.orderIdCounter().call();
        const totalCount = parseInt(counter ? counter.toString() : '0');
        setTotalOrderCount(totalCount);

        for (let i = totalCount; i >= 1; i--) {
          const idStr = i.toString();
          if (!idsList.includes(idStr)) {
            idsList.push(idStr);
          }
        }
      } catch (err) {
        console.warn('orderIdCounter call warning:', err);
      }

      // Reverse IDs so newest orders load first
      const reversedIds = Array.from(new Set(idsList)).sort((a, b) => parseInt(b) - parseInt(a));
      setAllCandidateIds(reversedIds);
      setCurrentPage(1);
    } catch (err) {
      console.error('Error discovering order IDs:', err);
    } finally {
      setLoadingOrders(false);
    }
  }, [contract, web3]);

  // 2. Fetch Order Batch for Current Page (30 per page with parallel RPC)
  const loadPageOrders = useCallback(async (page, candidateIds) => {
    if (!contract || !web3 || candidateIds.length === 0) {
      setDisplayedOrders([]);
      return;
    }

    setLoadingOrders(true);
    try {
      const startIndex = (page - 1) * PAGE_SIZE;
      const targetBatchIds = candidateIds.slice(startIndex, startIndex + PAGE_SIZE);

      // Execute parallel calls using Promise.all for fast 30-item loading
      const promises = targetBatchIds.map(async (orderId) => {
        try {
          const rawOrder = await contract.methods.orders(orderId).call();
          if (!rawOrder) return null;

          const id = rawOrder.id !== undefined ? rawOrder.id : rawOrder[0];
          const seller = rawOrder.seller !== undefined ? rawOrder.seller : rawOrder[1];
          const totalTokens = rawOrder.totalTokens !== undefined ? rawOrder.totalTokens : rawOrder[2];
          const remainingTokens = rawOrder.remainingTokens !== undefined ? rawOrder.remainingTokens : rawOrder[3];
          const price = rawOrder.price !== undefined ? rawOrder.price : rawOrder[4];
          const bonusPercent = rawOrder.bonusPercent !== undefined ? rawOrder.bonusPercent : rawOrder[5];
          const feeBNB = rawOrder.feeBNB !== undefined ? rawOrder.feeBNB : rawOrder[6];
          const active = rawOrder.active !== undefined ? rawOrder.active : rawOrder[7];

          const remainingStr = remainingTokens ? remainingTokens.toString() : '0';
          const isActive = Boolean(active);

          if (isActive && remainingStr !== '0') {
            const priceNum = parseFloat(price ? price.toString() : '1') || 1;
            const remainingFormatted = web3.utils.fromWei(remainingStr, 'ether');
            const remainingNum = parseFloat(remainingFormatted) || 0;
            const totalFormatted = web3.utils.fromWei(totalTokens ? totalTokens.toString() : '0', 'ether');
            const feeFormatted = web3.utils.fromWei(feeBNB ? feeBNB.toString() : '0', 'ether');
            const totalUsdtValue = (remainingNum / priceNum).toFixed(2);

            return {
              id: (id || orderId).toString(),
              seller: seller ? seller.toString() : '',
              totalTokens: totalFormatted,
              remainingTokens: remainingFormatted,
              totalUsdtValue: totalUsdtValue,
              price: price ? price.toString() : '1',
              bonusPercent: bonusPercent ? bonusPercent.toString() : '0',
              feeBNB: feeFormatted,
              active: isActive,
              usdtPerToken: (1 / priceNum).toFixed(6),
              isMyOrder: account && seller ? seller.toString().toLowerCase() === account.toLowerCase() : false
            };
          }
        } catch (err) {
          console.error(`Error loading order #${orderId}:`, err);
        }
        return null;
      });

      const loadedBatch = await Promise.all(promises);
      const validOrders = loadedBatch.filter(Boolean);
      setDisplayedOrders(validOrders);
    } catch (err) {
      console.error('Error loading page orders:', err);
    } finally {
      setLoadingOrders(false);
    }
  }, [contract, web3, account]);

  // Dynamic getOffer(buyAmount) lookup
  useEffect(() => {
    const fetchDynamicOffer = async () => {
      if (contract && buyAmount && parseFloat(buyAmount) > 0) {
        try {
          const tokenWei = web3.utils.toWei(buyAmount, 'ether');
          const res = await contract.methods.getOffer(tokenWei).call();
          const b = res.bonus !== undefined ? res.bonus.toString() : res[0].toString();
          const f = res.fee !== undefined ? res.fee.toString() : res[1].toString();
          setOfferBonus(b);
          setOfferFee(web3.utils.fromWei(f, 'ether'));
        } catch (err) {
          console.warn('getOffer error:', err);
        }
      } else {
        setOfferBonus(null);
        setOfferFee(null);
      }
    };
    fetchDynamicOffer();
  }, [contract, buyAmount, web3]);

  // Initial load when contract is ready
  useEffect(() => {
    if (contract && web3) {
      discoverAllOrderIds();
    }
  }, [contract, web3, discoverAllOrderIds]);

  // Trigger page load when currentPage or candidate IDs change
  useEffect(() => {
    if (allCandidateIds.length > 0) {
      loadPageOrders(currentPage, allCandidateIds);
    }
  }, [currentPage, allCandidateIds, loadPageOrders]);

  // Load balances & check registration when wallet connects
  useEffect(() => {
    if (contract && usdtContract && account) {
      loadBalances();
      checkUserRegistration();
    }
  }, [contract, usdtContract, account, loadBalances, checkUserRegistration]);

  // Filtered Display List by Quick Search Term
  const filteredOrders = useMemo(() => {
    if (!searchTerm.trim()) return displayedOrders;
    const term = searchTerm.trim().toLowerCase();
    return displayedOrders.filter(
      (order) =>
        order.id.includes(term) ||
        order.seller.toLowerCase().includes(term)
    );
  }, [displayedOrders, searchTerm]);

  const totalPages = Math.ceil(allCandidateIds.length / PAGE_SIZE) || 1;

  const handleCopy = (address) => {
    navigator.clipboard.writeText(address);
    setCopiedAddress(address);
    setTimeout(() => setCopiedAddress(null), 2000);
  };

  const handleRegisterUser = async () => {
    if (!account) {
      alert('Please connect your wallet first');
      return;
    }
    setRegistering(true);
    try {
      const method = contract.methods.register('0x0000000000000000000000000000000000000000');
      await sendContractTransaction(web3Context, method);
      alert('Address registered successfully on contract!');
      setIsRegistered(true);
    } catch (err) {
      console.error('Registration failed:', err);
      alert('Registration failed: ' + formatTransactionError(err));
    } finally {
      setRegistering(false);
    }
  };

  const handleBuyOrder = async () => {
    if (!account) {
      alert('Please connect your Web3 wallet to execute buy order');
      return;
    }

    if (!isRegistered) {
      alert('Your address must be registered on contract before executing buy orders. Please click "Instant Register Address".');
      return;
    }

    if (!selectedOrder || !buyAmount) {
      alert('Please select an order and enter an amount to buy');
      return;
    }

    const buyAmtNum = parseFloat(buyAmount);
    if (isNaN(buyAmtNum) || buyAmtNum <= 0) {
      alert('Please enter a valid positive token amount');
      return;
    }

    if (buyAmtNum > parseFloat(selectedOrder.remainingTokens)) {
      alert('Amount exceeds available tokens in this order');
      return;
    }

    // Determine effective fee BNB and bonus
    const effectiveFeeBNB = (offerFee !== null && parseFloat(offerFee) > parseFloat(selectedOrder.feeBNB))
      ? offerFee
      : selectedOrder.feeBNB;

    if (parseFloat(bnbBalance) < parseFloat(effectiveFeeBNB)) {
      alert(`Insufficient BNB balance for network fee! Required: ${effectiveFeeBNB} BNB, Available: ${bnbBalance} BNB`);
      return;
    }

    // Contract calculation: uint256 usdtAmount = tokenAmount / o.price
    const amountWei = web3.utils.toWei(buyAmount, 'ether');
    const amountWeiBig = window.BigInt(amountWei);
    const priceBig = window.BigInt(selectedOrder.price);

    if (priceBig === 0n) {
      alert('Invalid order price');
      return;
    }

    const usdtAmountWeiBig = amountWeiBig / priceBig;
    const usdtRequiredFormatted = web3.utils.fromWei(usdtAmountWeiBig.toString(), 'ether');

    if (parseFloat(usdtRequiredFormatted) > parseFloat(usdtBalance)) {
      alert(`Insufficient USDT balance! You need ${usdtRequiredFormatted} USDT but have ${usdtBalance} USDT.`);
      return;
    }

    setLoadingBuy(true);
    try {
      // Step 1: Approve USDT to contract
      await approveToken(web3Context, USDT_ADDRESS, CONTRACT_ADDRESS, usdtAmountWeiBig.toString());

      // Step 2: Execute buyOrder(orderId, tokenAmount) with BNB fee
      const feeBNBWei = web3.utils.toWei(effectiveFeeBNB || '0', 'ether');
      const method = contract.methods.buyOrder(selectedOrder.id, amountWei);
      const options = { value: feeBNBWei };

      await sendContractTransaction(web3Context, method, options);

      alert(`Successfully purchased ${buyAmount} tokens from Order #${selectedOrder.id}!`);
      setBuyAmount('');
      setSelectedOrder(null);
      loadPageOrders(currentPage, allCandidateIds);
      loadBalances();
    } catch (error) {
      console.error('Error buying order:', error);
      alert('Failed to execute purchase: ' + formatTransactionError(error));
    } finally {
      setLoadingBuy(false);
    }
  };

  const calculateBreakdown = () => {
    if (!selectedOrder || !buyAmount || isNaN(parseFloat(buyAmount))) {
      return { usdtCost: '0.00', bonusTokens: '0.00', totalTokensReceived: '0.00', feeBNB: selectedOrder ? selectedOrder.feeBNB : '0', bonusPct: selectedOrder ? selectedOrder.bonusPercent : '0' };
    }

    const amt = parseFloat(buyAmount);
    const price = parseFloat(selectedOrder.price) || 1;
    const usdtCost = (amt / price).toFixed(4);

    const bonusPct = offerBonus !== null ? parseFloat(offerBonus) : (parseFloat(selectedOrder.bonusPercent) || 0);
    const bonusTokens = ((amt * bonusPct) / 100).toFixed(4);
    const totalTokensReceived = (amt + parseFloat(bonusTokens)).toFixed(4);

    const effectiveFee = offerFee !== null && parseFloat(offerFee) > parseFloat(selectedOrder.feeBNB)
      ? offerFee
      : selectedOrder.feeBNB;

    return { usdtCost, bonusTokens, totalTokensReceived, feeBNB: effectiveFee, bonusPct };
  };

  const setPercentage = (pct) => {
    if (!selectedOrder) return;
    const maxVal = parseFloat(selectedOrder.remainingTokens);
    const calculated = (maxVal * (pct / 100)).toFixed(4);
    setBuyAmount(calculated);
  };

  const breakdown = calculateBreakdown();

  return (
    <div className="buy-order-container">
      {/* Header Banner */}
      <div className="buy-header-banner">
        <div className="banner-title">
          <h2>P2P Token Marketplace</h2>
          <p>Browse active trade orders (30 per page), select an order, and execute instant purchases.</p>
        </div>
        <div className="balance-chips">
          <div className="chip">
            <span className="chip-label">BNB Fee Bal</span>
            <span className="chip-val bnb-val">{parseFloat(bnbBalance).toFixed(4)} BNB</span>
          </div>
          <div className="chip">
            <span className="chip-label">USDT Balance</span>
            <span className="chip-val usdt-val">{parseFloat(usdtBalance).toFixed(2)} USDT</span>
          </div>
          <div className="chip">
            <span className="chip-label">Token Balance</span>
            <span className="chip-val token-val">{parseFloat(tokenBalance).toFixed(2)} Tokens</span>
          </div>
        </div>
      </div>

      {/* Registration Callout */}
      {account && !isRegistered && (
        <div className="registration-callout">
          <div className="reg-info">
            <h4>⚠️ Address Registration Required</h4>
            <p>Your wallet address must be registered on the contract before executing buy orders.</p>
          </div>
          <button className="btn-register-now" onClick={handleRegisterUser} disabled={registering}>
            {registering ? 'Registering...' : '📝 Instant Register Address'}
          </button>
        </div>
      )}

      {/* Quick Search & Refresh Bar */}
      <div className="search-bar-card">
        <div className="search-input-wrapper">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search Order ID or Seller Address..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="clear-search-btn" onClick={() => setSearchTerm('')}>✕</button>
          )}
        </div>

        <button className="btn-refresh" onClick={discoverAllOrderIds} disabled={loadingOrders}>
          {loadingOrders ? 'Loading Orders...' : '🔄 Refresh Orders'}
        </button>
      </div>

      {/* Main Grid: Paginated 30-Order Cards & Selected Order Panel */}
      <div className="marketplace-layout">
        {/* Available Orders Section */}
        <div className="orders-container">
          <div className="orders-count-header">
            <h3>Active Open Orders ({filteredOrders.length} shown)</h3>
            <span className="count-badge">
              Page {currentPage} of {totalPages} ({allCandidateIds.length} Active / {totalOrderCount} Total Contract Orders)
            </span>
          </div>

          {loadingOrders ? (
            <div className="loading-box">
              <div className="spinner"></div>
              <p>Loading 30 active orders from BSC Mainnet...</p>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="no-orders-box">
              <div className="no-orders-icon">📦</div>
              <h4>No active open orders found on this page</h4>
              <p>
                {allCandidateIds.length === 0
                  ? 'No sell orders have been listed on contract 0x28124CaeF97Bc376998dD5e7CCc3B86d41e1008A yet. Click "Sell Tokens" to list the first order!'
                  : 'Try changing page or clearing search term.'}
              </p>
            </div>
          ) : (
            <div className="orders-grid">
              {filteredOrders.map((order) => {
                const isSelected = selectedOrder?.id === order.id;
                return (
                  <div
                    key={order.id}
                    className={`order-card-modern ${isSelected ? 'selected' : ''} ${order.isMyOrder ? 'my-order' : ''}`}
                    onClick={() => {
                      setSelectedOrder(order);
                      setBuyAmount('');
                    }}
                  >
                    <div className="card-top">
                      <div className="order-id">
                        <span>Order #{order.id}</span>
                        {order.isMyOrder && <span className="tag my-tag">Mine</span>}
                      </div>
                      <div className="bonus-tag">
                        +{order.bonusPercent}% Bonus
                      </div>
                    </div>

                    <div className="card-metrics">
                      <div className="metric">
                        <span className="label">Order USDT Value</span>
                        <span className="val highlight-usdt">{order.totalUsdtValue} USDT</span>
                      </div>
                      <div className="metric">
                        <span className="label">Available Tokens</span>
                        <span className="val highlight-val">{parseFloat(order.remainingTokens).toLocaleString()} Tokens</span>
                      </div>
                      <div className="metric">
                        <span className="label">Exchange Rate</span>
                        <span className="val">{parseFloat(order.price).toLocaleString()} Tokens/USDT</span>
                      </div>
                      <div className="metric">
                        <span className="label">BNB Fee</span>
                        <span className="val">{order.feeBNB} BNB</span>
                      </div>
                    </div>

                    <div className="card-footer">
                      <div className="seller-info">
                        <span className="label">Seller:</span>
                        <span className="address-code" title={order.seller}>
                          {order.seller ? `${order.seller.slice(0, 6)}...${order.seller.slice(-4)}` : '0x...'}
                        </span>
                        <button
                          className="copy-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopy(order.seller);
                          }}
                        >
                          {copiedAddress === order.seller ? '✓' : '📋'}
                        </button>
                      </div>
                      <button
                        className="select-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedOrder(order);
                          setBuyAmount('');
                        }}
                      >
                        🛒 Buy ({order.totalUsdtValue} USDT)
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* 30-Item Pagination Controls */}
          {totalPages > 1 && (
            <div className="pagination-bar">
              <button
                className="page-btn"
                disabled={currentPage === 1 || loadingOrders}
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              >
                ◀ Previous 30
              </button>

              <span className="page-info">
                Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
              </span>

              <button
                className="page-btn"
                disabled={currentPage === totalPages || loadingOrders}
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              >
                Next 30 ▶
              </button>
            </div>
          )}
        </div>

        {/* Selected Order Buy Panel */}
        {selectedOrder && (
          <div className="buy-sidebar-panel">
            <div className="panel-header">
              <h3>Buy Order #{selectedOrder.id}</h3>
              <button className="close-btn" onClick={() => setSelectedOrder(null)}>✕</button>
            </div>

            <div className="selected-summary">
              <div className="summary-row">
                <span>Seller Address:</span>
                <span className="code">
                  {selectedOrder.seller ? `${selectedOrder.seller.slice(0, 8)}...${selectedOrder.seller.slice(-6)}` : '0x...'}
                </span>
              </div>
              <div className="summary-row">
                <span>Total Order USDT Value:</span>
                <span className="usdt-cost">{selectedOrder.totalUsdtValue} USDT</span>
              </div>
              <div className="summary-row">
                <span>Remaining Tokens:</span>
                <span className="val">{parseFloat(selectedOrder.remainingTokens).toLocaleString()} Tokens</span>
              </div>
              <div className="summary-row">
                <span>Exchange Rate:</span>
                <span className="val">1 USDT = {selectedOrder.price} Tokens</span>
              </div>
              <div className="summary-row">
                <span>Bonus Tier:</span>
                <span className="bonus-val">+{breakdown.bonusPct || selectedOrder.bonusPercent}%</span>
              </div>
              <div className="summary-row">
                <span>BNB Fee (msg.value):</span>
                <span className="fee-val">{breakdown.feeBNB} BNB</span>
              </div>
            </div>

            <div className="amount-input-group">
              <label>Input Token Quantity to Buy:</label>
              <div className="input-wrapper">
                <input
                  type="number"
                  placeholder="Enter amount (e.g. 1000)"
                  value={buyAmount}
                  onChange={(e) => setBuyAmount(e.target.value)}
                  max={selectedOrder.remainingTokens}
                  disabled={loadingBuy}
                />
                <button className="max-chip" onClick={() => setBuyAmount(selectedOrder.remainingTokens)}>
                  MAX
                </button>
              </div>

              <div className="pct-buttons">
                <button onClick={() => setPercentage(25)}>25%</button>
                <button onClick={() => setPercentage(50)}>50%</button>
                <button onClick={() => setPercentage(75)}>75%</button>
                <button onClick={() => setPercentage(100)}>100%</button>
              </div>
            </div>

            {/* Calculations Breakdown */}
            <div className="calculation-box">
              <div className="calc-row">
                <span>USDT Cost (tokenAmount / price):</span>
                <span className="usdt-cost">{breakdown.usdtCost} USDT</span>
              </div>
              <div className="calc-row">
                <span>Bonus Tokens Received:</span>
                <span className="bonus-tokens">+{breakdown.bonusTokens} Tokens</span>
              </div>
              <div className="calc-row">
                <span>BNB Fee Required:</span>
                <span className="bnb-fee">{breakdown.feeBNB} BNB</span>
              </div>
              <div className="calc-row total-row">
                <span>Total Tokens You Receive:</span>
                <span className="total-tokens">{breakdown.totalTokensReceived} Tokens</span>
              </div>
            </div>

            {account && !isRegistered && (
              <div className="warning-callout">
                ⚠️ Address not registered on-chain. Please click "Instant Register Address" above before buying.
              </div>
            )}

            <button
              className="execute-buy-btn"
              onClick={handleBuyOrder}
              disabled={loadingBuy || !buyAmount || parseFloat(buyAmount) <= 0 || !isRegistered}
            >
              {loadingBuy ? (
                <span className="spinner-text">Executing Buy Order...</span>
              ) : (
                `🛒 Approve USDT & Buy Order #${selectedOrder.id}`
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BuyOrder;